<script setup lang="ts">
import DOMPurify from 'dompurify';

import type { ChatMessage } from 'src/composables/useAiChat';

const props = defineProps<{
  message: ChatMessage;
  isStreaming?: boolean;
}>();

// Простой markdown-рендеринг (bold, italic, code, lists)
const renderedContent = computed(() => {
  if (!props.message.content) return '';

  // 1. Экранируем весь HTML из ответа AI (ответ — недоверенный контент)
  let text = props.message.content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks
  text = text.replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre><code>$2</code></pre>');

  // Inline code
  text = text.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bold
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Lists
  text = text.replace(/^- (.+)$/gm, '<li>$1</li>');
  text = text.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  // Line breaks
  text = text.replace(/\n/g, '<br>');

  // 2. Defense in depth: санитизируем результат перед v-html
  // (DOMPurify не имеет sanitize в Node без window — гард на случай, если компонент когда-либо
  // будет рендериться на сервере; сейчас компонент монтируется только клиентом через v-if isClient)
  if (!DOMPurify.isSupported) return text;
  return DOMPurify.sanitize(text);
});

const messageClass = computed(() => `ai-message--${props.message.role}`);
</script>

<template lang="pug">
.ai-message(:class="messageClass")
  //- Avatar
  .ai-message__avatar
    //- User icon
    svg(v-if="message.role === 'user'" width="16" height="16" viewBox="0 0 16 16" fill="none")
      circle(cx="8" cy="5" r="3" fill="currentColor")
      path(d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" fill="currentColor")

    //- AI icon
    svg(v-else width="16" height="16" viewBox="0 0 16 16" fill="none")
      path(d="M8 1C4.13 1 1 4.13 1 8s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7z" fill="currentColor" opacity="0.2")
      path(d="M8 3v4l3 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round")

  //- Content
  .ai-message__content
    //- User message
    .ai-message__text--user(v-if="message.role === 'user'") {{ message.content }}

    //- Assistant message
    .ai-message__text--assistant(
      v-else
      v-html="renderedContent"
    )

    //- Typing indicator
    .ai-message__typing(v-if="isStreaming && !message.content")
      span
      span
      span
</template>

<style lang="sass">
.ai-message
  display: flex
  gap: 10px
  margin-bottom: 12px

  &--user
    flex-direction: row-reverse

    .ai-message__avatar
      background: rgba(255, 255, 255, 0.15)
      color: white

    .ai-message__text--user
      background: rgba(255, 255, 255, 0.1)
      color: white
      border-radius: 16px 16px 4px 16px

  &--assistant
    .ai-message__avatar
      background: rgba(74, 222, 128, 0.15)
      color: #4ade80

    .ai-message__text--assistant
      background: rgba(255, 255, 255, 0.04)
      color: rgba(255, 255, 255, 0.9)
      border-radius: 16px 16px 16px 4px

  &__avatar
    width: 28px
    height: 28px
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0

  &__content
    max-width: 80%

  &__text--user,
  &__text--assistant
    padding: 10px 14px
    font-size: 13px
    line-height: 1.5
    word-break: break-word

  &__text--assistant
    :deep(code)
      background: rgba(255, 255, 255, 0.1)
      padding: 2px 5px
      border-radius: 4px
      font-size: 12px

    :deep(pre)
      background: rgba(0, 0, 0, 0.3)
      padding: 10px
      border-radius: 8px
      overflow-x: auto
      margin: 8px 0

    :deep(ul)
      padding-left: 18px
      margin: 6px 0

    :deep(li)
      margin-bottom: 4px

    :deep(strong)
      color: white

  &__typing
    display: flex
    gap: 4px
    padding: 12px 14px

    span
      width: 6px
      height: 6px
      border-radius: 50%
      background: rgba(255, 255, 255, 0.4)
      animation: typing 1.4s infinite

      &:nth-child(2)
        animation-delay: 0.2s

      &:nth-child(3)
        animation-delay: 0.4s

@keyframes typing
  0%, 60%, 100%
    opacity: 0.3
    transform: translateY(0)
  30%
    opacity: 1
    transform: translateY(-4px)
</style>
